import React, { useEffect, useRef, useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import './aiAssistPopover.scss';
import { Images } from '../../../utils';
import { CloseButton } from '../../../components';

/**
 * Phase-driven AI Assist dialog. The flow is two-stage to prevent accidental
 * overwrites of clinical content:
 *   IDLE       → user types instruction, clicks Generate Preview
 *   GENERATING → side-by-side original vs streaming AI output (read-only)
 *   PREVIEW    → side-by-side, three actions: Apply / Regenerate / Cancel
 *   ERROR      → error banner + Try Again
 *
 * Streaming chunks land in `previewText` (managed by parent), NOT directly
 * into the form field. Field is only updated when the user explicitly clicks
 * Apply Changes.
 */

export const AiAssistPhase = {
    IDLE: 'idle',
    GENERATING: 'generating',
    PREVIEW: 'preview',
    ERROR: 'error',
};

const MAX_INSTRUCTION_LENGTH = 1000;

const QUICK_PROMPTS = [
    { label: 'Fix grammar', text: 'Fix grammar and punctuation.' },
    { label: 'Make professional', text: 'Rewrite in a more professional clinical tone, preserving every detail.' },
    { label: 'Shorten', text: 'Make this shorter while keeping all clinical details and numbers.' },
    { label: 'Improve readability', text: 'Improve readability and split into clear short paragraphs.' },
];

const ABA_EXAMPLE_HINT =
    'e.g., "Client engaged in 8/10 receptive language trials with verbal prompts. Tantrum behavior observed twice during transitions."';

export const AiAssistPopover = ({
    open,
    onClose,
    onGenerate,
    onApply,
    onRegenerate,
    originalText = '',
    previewText = '',
    phase = AiAssistPhase.IDLE,
    errorMessage = '',
    lastInstruction = '',
}) => {
    const [instruction, setInstruction] = useState('');
    const inputRef = useRef(null);

    // Reset instruction when reopening into the IDLE phase. Don't reset
    // during phase transitions while the dialog stays open (Generate → Preview).
    useEffect(() => {
        if (open && phase === AiAssistPhase.IDLE) setInstruction('');
    }, [open, phase]);

    useEffect(() => {
        if (open && phase === AiAssistPhase.IDLE) {
            const t = setTimeout(() => inputRef.current?.focus(), 60);
            return () => clearTimeout(t);
        }
    }, [open, phase]);

    const sourceIsEmpty = !stripHtml(originalText).trim();
    const generateDisabled =
        !instruction.trim() ||
        sourceIsEmpty ||
        phase === AiAssistPhase.GENERATING;

    const handleGenerate = () => {
        if (generateDisabled) return;
        onGenerate(instruction.trim());
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleGenerate();
        }
    };

    const handleChip = (text) => {
        setInstruction(text);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    if (!open) return null;

    return (
        <div className="ai-assist-overlay" onClick={onClose}>
            <div className="ai-assist-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="ai-assist-header">
                    <div className="ai-assist-header-left">
                        <span className="ai-assist-header-icon">
                            <AutoAwesomeIcon />
                        </span>
                        <span className="ai-assist-header-title">AI Assist</span>
                        {phase === AiAssistPhase.GENERATING && (
                            <span className="ai-assist-generating-label">
                                Generating preview…
                            </span>
                        )}
                    </div>
                    <CloseButton handleCLic={onClose} />
                </div>

                {/* Body */}
                <div className="ai-assist-body">
                    {phase === AiAssistPhase.IDLE && (
                        <IdleBody
                            instruction={instruction}
                            setInstruction={setInstruction}
                            inputRef={inputRef}
                            onKeyDown={handleKeyDown}
                            onChip={handleChip}
                            sourceIsEmpty={sourceIsEmpty}
                        />
                    )}

                    {(phase === AiAssistPhase.GENERATING ||
                        phase === AiAssistPhase.PREVIEW) && (
                        <PreviewBody
                            originalText={originalText}
                            previewText={previewText}
                            phase={phase}
                            lastInstruction={lastInstruction}
                        />
                    )}

                    {phase === AiAssistPhase.ERROR && (
                        <ErrorBody errorMessage={errorMessage} />
                    )}
                </div>

                {/* Footer */}
                <div className="ai-assist-footer">
                    <FooterMeta phase={phase} instructionLength={instruction.length} />
                    <div className="ai-assist-footer-actions">
                        {phase === AiAssistPhase.IDLE && (
                            <>
                                <button
                                    type="button"
                                    className="ai-assist-btn-secondary"
                                    onClick={onClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="ai-assist-btn-primary"
                                    onClick={handleGenerate}
                                    disabled={generateDisabled}
                                >
                                    <AutoAwesomeIcon />
                                    Generate Preview
                                </button>
                            </>
                        )}

                        {phase === AiAssistPhase.GENERATING && (
                            <button
                                type="button"
                                className="ai-assist-btn-secondary"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                        )}

                        {phase === AiAssistPhase.PREVIEW && (
                            <>
                                <button
                                    style={{background: 'transparent', border: 'none', boxShadow: 'none', padding: 0}}
                                    type="button"
                                    className="ai-assist-btn-secondary"
                                    onClick={onClose}
                                >
                                    Keep Original
                                </button>
                                <button
                                    type="button"
                                    className="ai-assist-btn-secondary"
                                    onClick={onRegenerate}
                                >
                                    <RefreshIcon />
                                    Regenerate
                                </button>
                                <button
                                    type="button"
                                    className="ai-assist-btn-primary"
                                    onClick={onApply}
                                >
                                    <CheckIcon />
                                    Apply Changes
                                </button>
                            </>
                        )}

                        {phase === AiAssistPhase.ERROR && (
                            <>
                                <button
                                    type="button"
                                    className="ai-assist-btn-secondary"
                                    onClick={onClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="ai-assist-btn-primary"
                                    onClick={onRegenerate}
                                    disabled={!lastInstruction}
                                >
                                    <RefreshIcon />
                                    Try Again
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ─────────────── Subviews ─────────────── */

const IdleBody = ({ instruction, setInstruction, inputRef, onKeyDown, onChip, sourceIsEmpty }) => (
    <>
        <div className="ai-assist-info-banner">

            <img src={Images.infoCircle} alt="inco" style={{
                filter: 'brightness(0) saturate(100%) invert(40%) sepia(92%) saturate(3273%) hue-rotate(239deg) brightness(100%) contrast(94%)',
            }} />
            <p className="ai-assist-info-text">
                AI only rewrites existing content and does not create new clinical information.
                Numbers, names, and observed behaviors are preserved exactly.
            </p>
        </div>

        {sourceIsEmpty && (
            <div className="ai-assist-empty-warning">
                <span className="ai-assist-empty-warning-icon">
                    <WarningAmberRoundedIcon />
                </span>
                <div>
                    <p className="ai-assist-empty-warning-title">This field is empty</p>
                    <p className="ai-assist-empty-warning-text">
                        AI Assist needs existing content to rewrite. Add some session notes first — {ABA_EXAMPLE_HINT}
                    </p>
                </div>
            </div>
        )}

        <p className="ai-assist-quick-label">Quick prompts</p>
        <div className="ai-assist-chips">
            {QUICK_PROMPTS.map((p) => (
                <button
                    key={p.label}
                    type="button"
                    className={`ai-assist-chip${instruction === p.text ? ' selected' : ''}`}
                    onClick={() => onChip(p.text)}
                >
                    {p.label}
                </button>
            ))}
        </div>

        <textarea
            ref={inputRef}
            className="ai-assist-textarea"
            placeholder="Describe how AI should rewrite this field. e.g., Make this shorter. Fix grammar. Restructure into clear paragraphs."
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={onKeyDown}
            maxLength={MAX_INSTRUCTION_LENGTH}
            disabled={sourceIsEmpty}
            rows={3}
        />
    </>
);

const PreviewBody = ({ originalText, previewText, phase, lastInstruction }) => (
    <div>
        {lastInstruction && (
            <div className="ai-assist-instruction-banner">
                <p className="ai-assist-instruction-label">Instruction</p>
                <p className="ai-assist-instruction-text">{lastInstruction}</p>
            </div>
        )}

        <div className="ai-assist-comparison">
            <ComparisonPane
                title="Original"
                titleClass="original"
                text={originalText}
            />
            <ComparisonPane
                resetStyles={{background: '#FCFAFF', border:'none'}}
                title="AI Suggested"
                titleClass="suggested"
                text={previewText}
                showStreamingDot={phase === AiAssistPhase.GENERATING}
                emptyHint={phase === AiAssistPhase.GENERATING ? 'AI is writing…' : 'No suggestion generated.'}
            />
        </div>
    </div>
);

const ComparisonPane = ({ title, titleClass, text, showStreamingDot, emptyHint, resetStyles= {} }) => {
    const hasText = !!stripHtml(text).trim();
    return (
        <div className="ai-assist-pane" style={{ ...resetStyles }}>
            <div className="ai-assist-pane-header">
                <span className={`ai-assist-pane-title ${titleClass}`}>{title}</span>
                {showStreamingDot && <span className="ai-assist-streaming-dot" />}
            </div>
            {hasText ? (
                <div
                    className="ai-assist-pane-content"
                    dangerouslySetInnerHTML={{ __html: text }}
                />
            ) : (
                <p className="ai-assist-pane-empty">
                    {emptyHint || '(empty)'}
                </p>
            )}
        </div>
    );
};

const ErrorBody = ({ errorMessage }) => (
    <div className="ai-assist-error-banner">
        <span className="ai-assist-error-icon">
            <ErrorOutlineIcon />
        </span>
        <div>
            <p className="ai-assist-error-title">Generation failed</p>
            <p className="ai-assist-error-text">
                {errorMessage || 'AI generation failed. Try a simpler instruction or check your connection.'}
            </p>
        </div>
    </div>
);

const FooterMeta = ({ phase, instructionLength }) => {
    if (phase === AiAssistPhase.IDLE) {
        return (
            <span className="ai-assist-footer-meta">
                {instructionLength}/{MAX_INSTRUCTION_LENGTH} · ⌘/Ctrl+Enter to generate
            </span>
        );
    }
    if (phase === AiAssistPhase.GENERATING) {
        return (
            <div className="ai-assist-footer-streaming">
                <span className="ai-assist-footer-spinner" />
                <span className="ai-assist-footer-meta">Streaming AI output…</span>
            </div>
        );
    }
    if (phase === AiAssistPhase.PREVIEW) {
        return (
            <span className="ai-assist-preview-meta">
                Review the suggested version. <br/>
                Apply replaces the field; you can revert afterwards.
            </span>
        );
    }
    return <span />;
};

/* ─────────────── helpers ─────────────── */

/** Strip HTML tags to a plain-text approximation for emptiness checks. */
function stripHtml(html) {
    if (!html || typeof html !== 'string') return '';
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
}
